/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  hoodMetaReducerCreator.js
 */

import { falseReducer, nullReducer, payloadKeyReducer, trueReducer } from '@zcode/reducers'
import type { PriceChartMeta } from '@zcode/state-type'
import { combineReducers } from 'redux'
import { type NarrowedAction, handleActions } from 'redux-actions'
import type {
  CITY_NAME_CHANGED,
  CLEAR_CITY_NAME,
  CLEAR_HOOD_NAME,
  HOOD_NAME_CHANGED,
} from '../../../currentSearch/modules/location/constants'
import type * as detailConstants from '../../constants/detail'
import type * as localeConstants from '../../constants/locale'
import type { ActionTypes } from '../types'

type ActionConstants =
  | {
      errorFetching: typeof detailConstants.ERROR_FETCHING_HOOD_PRICE_CHART_DATA
      cityNameChanged: typeof CITY_NAME_CHANGED
      clearCityName: typeof CLEAR_CITY_NAME
      clearHoodName: typeof CLEAR_HOOD_NAME
      clearPriceChartData: typeof detailConstants.CLEAR_PRICE_CHART_DATA
      clearHoodPriceChartData: typeof detailConstants.CLEAR_HOOD_PRICE_CHART_DATA
      hoodNameChanged: typeof HOOD_NAME_CHANGED
      startFetching: typeof detailConstants.START_FETCHING_HOOD_PRICE_CHART_DATA
      doneFetching: typeof detailConstants.DONE_FETCHING_HOOD_PRICE_CHART_DATA
      setData: typeof detailConstants.SET_HOOD_PRICE_CHART_DATA
    }
  | {
      errorFetching: typeof localeConstants.ERROR_FETCHING_HOOD_PRICE_CHART_DATA
      cityNameChanged: typeof CITY_NAME_CHANGED
      clearCityName: typeof CLEAR_CITY_NAME
      clearHoodName: typeof CLEAR_HOOD_NAME
      clearPriceChartData: typeof localeConstants.CLEAR_PRICE_CHART_DATA
      clearHoodPriceChartData: typeof localeConstants.CLEAR_HOOD_PRICE_CHART_DATA
      hoodNameChanged: typeof HOOD_NAME_CHANGED
      startFetching: typeof localeConstants.START_FETCHING_HOOD_PRICE_CHART_DATA
      doneFetching: typeof localeConstants.DONE_FETCHING_HOOD_PRICE_CHART_DATA
      setData: typeof localeConstants.SET_HOOD_PRICE_CHART_DATA
    }

export const hoodMetaReducerCreator = ({
  errorFetching,
  cityNameChanged,
  clearCityName,
  clearHoodName,
  clearPriceChartData,
  clearHoodPriceChartData,
  hoodNameChanged,
  startFetching,
  doneFetching,
  setData,
}: ActionConstants) =>
  combineReducers<PriceChartMeta, ActionTypes>({
    error: handleActions<PriceChartMeta['error'], ActionTypes>(
      {
        [errorFetching]: trueReducer,
        [cityNameChanged]: falseReducer,
        [clearCityName]: falseReducer,
        [clearHoodName]: falseReducer,
        [clearPriceChartData]: falseReducer,
        [clearHoodPriceChartData]: falseReducer,
        [hoodNameChanged]: falseReducer,
        [startFetching]: falseReducer,
      },
      false
    ),
    fetching: handleActions<PriceChartMeta['fetching'], ActionTypes>(
      {
        [startFetching]: trueReducer,
        [cityNameChanged]: falseReducer,
        [clearCityName]: falseReducer,
        [clearHoodName]: falseReducer,
        [clearPriceChartData]: falseReducer,
        [clearHoodPriceChartData]: falseReducer,
        [doneFetching]: falseReducer,
        [hoodNameChanged]: falseReducer,
      },
      false
    ),
    fetchingKey: handleActions<PriceChartMeta['fetchingKey'], ActionTypes>(
      {
        [startFetching]: payloadKeyReducer<
          PriceChartMeta['fetchingKey'],
          'key',
          NarrowedAction<ActionTypes, typeof startFetching>['payload']
        >('key'),
        [cityNameChanged]: nullReducer,
        [clearCityName]: nullReducer,
        [clearHoodName]: nullReducer,
        [clearPriceChartData]: nullReducer,
        [clearHoodPriceChartData]: nullReducer,
        [doneFetching]: nullReducer,
        [hoodNameChanged]: nullReducer,
      },
      null
    ),
    currentKey: handleActions<PriceChartMeta['currentKey'], ActionTypes>(
      {
        [setData]: payloadKeyReducer<
          PriceChartMeta['currentKey'],
          'key',
          NarrowedAction<ActionTypes, typeof setData>['payload']
        >('key'),
        [cityNameChanged]: nullReducer,
        [clearCityName]: nullReducer,
        [clearHoodName]: nullReducer,
        [clearPriceChartData]: nullReducer,
        [clearHoodPriceChartData]: nullReducer,
        [hoodNameChanged]: nullReducer,
      },
      null
    ),
  })
